import type { CharacterStats } from "./types.js";

// M17 — no level cap. The "practical" ceiling emerges from the bestiary
// (finite mob XP × the steep curve), not from a constant (REFONTE-STATS §8).
export const XP_LOSS_ON_DEATH_PCT = 0.05;
export const STARTING_LEVEL = 1;

// M17 — group XP bonus: the mob is worth +20% total per extra party member,
// then split equally (REFONTE-COMBAT §6). Solo = 100%, ×2 = 120% (60% each),
// ×6 = 200% (33% each). Grouping is always worth it, never mandatory.
export const GROUP_XP_BONUS_PER_MEMBER = 0.2;
export function groupXpTotal(base: number, memberCount: number): number {
  if (memberCount <= 1) return Math.max(0, Math.floor(base));
  return Math.floor(base * (1 + GROUP_XP_BONUS_PER_MEMBER * (memberCount - 1)));
}

// M17 — degressive stat points per level-up: the antidote to quadratic pool
// inflation under unlimited levels. 5/level up to 50, then 4 → 100,
// 3 → 150, 2 → 200, 1 beyond. `points granted on reaching level L`.
export function pointsForLevel(level: number): number {
  if (level <= 50) return 5;
  if (level <= 100) return 4;
  if (level <= 150) return 3;
  if (level <= 200) return 2;
  return 1;
}

export type StatKey = keyof CharacterStats;
export const STAT_KEYS: readonly StatKey[] = [
  "str",
  "dex",
  "con",
  "int",
  "fai",
  "rap",
] as const;

export interface ProgressionState {
  level: number;
  xp: number;
  unspentPoints: number;
}

export interface ProgressionGainResult {
  levelsGained: number;
  newLevel: number;
}

export function xpToReach(level: number): number {
  if (level <= 0) return 0;
  return Math.floor(100 * Math.pow(level, 2.5));
}

export function applyXpGain(
  state: ProgressionState,
  amount: number,
): ProgressionGainResult {
  const gain = Math.max(0, Math.floor(amount));
  if (gain === 0) return { levelsGained: 0, newLevel: state.level };
  state.xp += gain;
  let levelsGained = 0;
  // No cap — the loop terminates because xpToReach grows with the level.
  while (state.xp >= xpToReach(state.level)) {
    state.xp -= xpToReach(state.level);
    state.level += 1;
    state.unspentPoints += pointsForLevel(state.level);
    levelsGained += 1;
  }
  return { levelsGained, newLevel: state.level };
}

export function applyDeathXpLoss(state: ProgressionState): number {
  const loss = Math.floor(state.xp * XP_LOSS_ON_DEATH_PCT);
  state.xp = Math.max(0, state.xp - loss);
  return loss;
}

export function isStatKey(key: string): key is StatKey {
  return (STAT_KEYS as readonly string[]).includes(key);
}
